import * as React from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button
} from '@mui/material';
import {
  Add as AddIcon
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type { QualityCheckNode } from '@/types/workflow';
import type { QualityChecksCardProps } from '@/types/ui';
import QualityCheckNodeItem from '../nodes/QualityCheckNodeItem';

interface EditState {
  node: QualityCheckNode | null;
  prompt: string;
  passFailPrompt: string;
}

export default function QualityChecksCard({ 
  qualityCheckNodes, 
  setQualityCheckNodes 
}: QualityChecksCardProps) {
  const [editState, setEditState] = React.useState<EditState>({
    node: null,
    prompt: '',
    passFailPrompt: ''
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = qualityCheckNodes.findIndex((item: QualityCheckNode) => item.id === active.id);
      const newIndex = qualityCheckNodes.findIndex((item: QualityCheckNode) => item.id === over?.id);
      setQualityCheckNodes(arrayMove(qualityCheckNodes, oldIndex, newIndex));
    }
  };

  const handleAddNode = () => {
    // Don't add new node if any item is being edited
    if (editState.node) {
      return;
    }
    
    const newNode: QualityCheckNode = {
      id: `qc-${crypto.randomUUID()}`,
      prompt: '',
      passFailPrompt: ''
    };
    setQualityCheckNodes([...qualityCheckNodes, newNode]);
    // Automatically start editing the new node
    setEditState({
      node: newNode,
      prompt: '',
      passFailPrompt: ''
    });
  };

  const handleEditNode = (node: QualityCheckNode) => {
    // If there's already an editing node, cancel it first
    if (editState.node && editState.node.id !== node.id) {
      setEditState({
        node: null,
        prompt: '',
        passFailPrompt: ''
      });
    }
    setEditState({
      node: node,
      prompt: node.prompt,
      passFailPrompt: node.passFailPrompt
    });
  };

  const handleSaveNode = (nodeId: string, newPrompt: string, newPassFailPrompt: string) => {
    // Don't save if prompt is empty or only whitespace
    if (!newPrompt.trim()) {
      return;
    }
    
    setQualityCheckNodes(qualityCheckNodes.map(node => 
      node.id === nodeId ? { 
        ...node, 
        prompt: newPrompt.trim(),
        passFailPrompt: newPassFailPrompt.trim()
      } : node
    ));
    setEditState({
      node: null,
      prompt: '',
      passFailPrompt: ''
    });
  };

  const handleCancelEdit = () => {
    // If the current editing node has an empty prompt, remove it from the list
    if (editState.node && !editState.prompt.trim()) {
      setQualityCheckNodes(qualityCheckNodes.filter(node => node.id !== editState.node!.id));
    }
    setEditState({
      node: null,
      prompt: '',
      passFailPrompt: ''
    });
  };

  const handleDeleteNode = (nodeId: string) => {
    setQualityCheckNodes(qualityCheckNodes.filter(node => node.id !== nodeId));
  };

  return (
    <Paper sx={{ 
      padding: 4,
      backgroundColor: 'background.paper',
      borderRadius: 2,
      boxShadow: 1,
      maxWidth: 800
    }}>
      <Typography 
        variant="h4" 
        component="h1" 
        sx={{ 
          color: 'text.primary', 
          fontWeight: 'bold',
          marginBottom: 2,
          fontSize: '1.5rem'
        }}
      >
        Quality Checks
      </Typography>
        <Typography 
          variant="body1" 
          sx={{ 
            color: 'text.secondary',
            marginBottom: 4,
            fontSize: '0.875rem',
            lineHeight: 1.6
          }}
        >
          Quality Check nodes are run in parallel. They will each get the original document and the summary as context. 
          The output of the node will be visible when reviewing the document.
          <br />
          Pass/Fail prompts are optional. The prompt should describe how the AI can determine whether the summary has passed given the output of the Quality Check prompt.
          <br />
          If all checks pass for a given workflow, the summary will display '✅Passed' in the list of documents. If any of the checks fail, it will display '⛔Failed'.
          <br />
          Nodes without a Pass/Fail prompt will display their output when inspecting the summary, but will not affect the status of the summary.
        </Typography>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext 
            items={qualityCheckNodes.map(node => node.id)}
            strategy={verticalListSortingStrategy}
          >
            <Stack>
              {qualityCheckNodes.map((node) => (
                <Box key={node.id} sx={{ position: 'relative' }}>
                  <QualityCheckNodeItem
                    node={node}
                    onEdit={handleEditNode}
                    onDelete={handleDeleteNode}
                    onSave={handleSaveNode}
                    onCancel={handleCancelEdit}
                    isEditing={editState.node?.id === node.id}
                    editPrompt={editState.prompt}
                    editPassFailPrompt={editState.passFailPrompt}
                    setEditPrompt={(prompt) => setEditState(prev => ({ ...prev, prompt }))}
                    setEditPassFailPrompt={(passFailPrompt) => setEditState(prev => ({ ...prev, passFailPrompt }))}
                  />
                </Box>
              ))}
            </Stack>
          </SortableContext>
        </DndContext>
        
        <Box sx={{ marginTop: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddNode}
            disabled={Boolean(editState.node)}
            sx={{
              minWidth: 120
            }}
          >
            Add Quality Check
          </Button>
        </Box>
    </Paper>
  );
}
